import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  RefreshCw, AlertCircle, AlertTriangle, Activity, Server, History,
} from "lucide-react";

import { API } from "../../components/superadmin/system-status/constants";
import { StatusBadge } from "../../components/superadmin/system-status/StatusBadge";
import { SectionHeader } from "../../components/superadmin/system-status/SectionHeader";
import { HealthOverviewBanner } from "../../components/superadmin/system-status/HealthOverviewBanner";
import { ExternalServicesCard } from "../../components/superadmin/system-status/ExternalServicesCard";
import { DependenciesSection } from "../../components/superadmin/system-status/DependenciesSection";
import { HealthHistoryCard } from "../../components/superadmin/system-status/HealthHistoryCard";
import { ScreeningJobsCard } from "../../components/superadmin/system-status/ScreeningJobsCard";
import { ExtractionFailuresCard } from "../../components/superadmin/system-status/ExtractionFailuresCard";
import { EmailMetricsCard } from "../../components/superadmin/system-status/EmailMetricsCard";

export default function SuperAdminSystemStatus() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  // Filled by ExternalServicesCard whenever it (re)loads. We need it here so
  // the top "Estado actual" banner can show a single consolidated count
  // covering both internal deps AND external services.
  const [externalSummary, setExternalSummary] = useState(null);

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/health`, { withCredentials: true });
      setReport(res.data);
      setError(null);
      setLastFetched(new Date());
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Error al consultar /api/health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    const interval = setInterval(fetchHealth, 30_000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="system-status-loading">
        <RefreshCw className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  if (error && !report) {
    return (
      <div
        className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center"
        data-testid="system-status-error"
      >
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-300">{error}</p>
        <button
          onClick={fetchHealth}
          data-testid="system-status-retry-btn"
          className="mt-4 px-4 py-2 bg-red-500/20 text-red-300 rounded-lg hover:bg-red-500/30"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const status = report?.status || "unhealthy";
  const missingCritical = report?.missing_critical || [];
  const missingImportant = report?.missing_important || [];

  return (
    <div className="space-y-6" data-testid="system-status-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Server className="w-6 h-6 text-amber-400" />
            Estado del sistema
          </h1>
          <p className="text-slate-400 mt-1 text-sm">
            Estado en tiempo real de las dependencias críticas del backend
            (OCR, generación de PDFs, descarga de CURP).
          </p>
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge status={status} />
          <button
            onClick={fetchHealth}
            data-testid="system-status-refresh-btn"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
            disabled={loading}
            title="Refrescar ahora"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {(missingCritical.length > 0 || missingImportant.length > 0) && (
        <div
          data-testid="system-status-alert"
          className={`rounded-xl border p-4 ${
            missingCritical.length > 0
              ? "bg-red-500/10 border-red-500/30 text-red-200"
              : "bg-amber-500/10 border-amber-500/30 text-amber-200"
          }`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="text-sm space-y-1">
              {missingCritical.length > 0 && (
                <p>
                  <strong>Críticos faltantes:</strong>{" "}
                  <span className="font-mono">{missingCritical.join(", ")}</span>
                </p>
              )}
              {missingImportant.length > 0 && (
                <p>
                  <strong>Importantes faltantes:</strong>{" "}
                  <span className="font-mono">{missingImportant.join(", ")}</span>
                </p>
              )}
              <p className="text-xs opacity-80 mt-1">
                Reconstruye la imagen Docker (todas las deps están en el Dockerfile)
                o ejecuta <span className="font-mono">bash /app/start.sh</span> para
                ver instrucciones de instalación manual.
              </p>
            </div>
          </div>
        </div>
      )}

      <HealthOverviewBanner
        report={report}
        externalSummary={externalSummary}
      />

      {/* ════════ ESTADO ACTUAL ════════
          Lo que está OK / roto AHORA. */}
      <SectionHeader
        icon={Activity}
        title="Estado actual"
        subtitle="Salud en vivo de cada servicio. Cambia en cuanto algo se rompe."
      />

      <ExternalServicesCard onSummaryChange={setExternalSummary} />

      <DependenciesSection dependencies={report?.dependencies} />

      {/* ════════ HISTORIAL ════════
          Datos a lo largo del tiempo: uptime de los servicios y errores
          recientes que ya pasaron. */}
      <SectionHeader
        icon={History}
        title="Historial"
        subtitle="Tendencias y eventos pasados — útil para diagnosticar problemas intermitentes."
      />

      <HealthHistoryCard />

      <ScreeningJobsCard />

      <EmailMetricsCard />

      <ExtractionFailuresCard />

      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" />
          <span>Auto-actualización cada 30 segundos</span>
        </div>
        {lastFetched && (
          <span data-testid="system-status-last-fetched">
            Actualizado: {lastFetched.toLocaleString("es-MX")}
          </span>
        )}
      </div>
    </div>
  );
}
